import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronLeft, ChevronRight, Plus, CalendarDays } from 'lucide-react';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { SelectField } from '@/features/clients/SelectField';
import { cn } from '@/lib/cn';
import { useAuthStore } from '@/stores/auth';
import { useCalendarHours } from '@/api/settings';
import { useSessions, useUpdateSession, type SessionRecord } from '@/api/sessions';
import { useClientNames } from '@/api/clients';
import { useUserDirectory } from '@/api/users';
import { useActiveSessionTypes } from '@/api/referenceData';
import {
    type CalendarView,
    dayOfWeekMon0,
    defaultView,
    layoutLanes,
    offsetForView,
    toIsoDate,
    weekDates,
} from '@/lib/calendar';
import { SessionBlock } from './SessionBlock';
import { SessionDialog } from './SessionDialog';
import { Skeleton } from '@/components/ui/Skeleton';

const SLOT_HEIGHT_PX = 16;

const VIEW_OPTIONS: { value: CalendarView; label: string }[] = [
    { value: 'workweek', label: 'Workweek' },
    { value: 'week',     label: 'Week' },
    { value: 'day',      label: 'Day' },
];

const WEEKDAY_LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

function formatHour(h: number): string {
    const hour12 = ((h + 11) % 12) + 1;
    const suffix = h < 12 || h === 24 ? 'am' : 'pm';
    return `${hour12}${suffix}`;
}

function formatDayHeader(d: Date): { label: string; date: string } {
    return {
        label: WEEKDAY_LABELS[dayOfWeekMon0(d)],
        date: `${d.getMonth() + 1}/${d.getDate()}`,
    };
}

function isSameDay(a: Date, b: Date): boolean {
    return (
        a.getFullYear() === b.getFullYear()
        && a.getMonth() === b.getMonth()
        && a.getDate() === b.getDate()
    );
}

interface SlotPick {
    date: string;
    hour: number;
    minute: number;
}

/**
 * One of {create-on-empty-slot, edit-existing-session} opens the same
 * unified SessionDialog. Keeping it in a single state field eliminates
 * the "two dialogs open at once" bug the prior three-dialog split was
 * prone to, and it simplifies the close/reset lifecycle.
 */
type DialogState =
    | { kind: 'closed' }
    | { kind: 'create'; slot: SlotPick };

type MoveOverride = { date: string; startH: number; startM: number; dur: number };

export function SchedulingPage() {
    const navigate = useNavigate();
    // Weekends open in Week view so the current day is always on screen
    // (Mon–Fri would hide a Sat/Sun login). Computed once on mount.
    const [view, setView] = useState<CalendarView>(() => defaultView());
    const [offset, setOffset] = useState(0);
    const [dialog, setDialog] = useState<DialogState>({ kind: 'closed' });
    const [staffFilter, setStaffFilter] = useState<string>('');
    const [clientFilter, setClientFilter] = useState<string>('');
    /*
     * Status filter: default 'active' hides canceled occurrences — planning
     * is the common case and a wall of struck-through blocks just muddies
     * the calendar. 'all' shows everything; 'canceled' isolates both
     * Canceled-Staff and Canceled-Client for audit / review passes.
     * Per-status drill-down (Scheduled-only, Finalized-only, …) hasn't been
     * asked for and would mostly hide context a scheduler wants to see.
     */
    const [statusFilter, setStatusFilter] = useState<'active' | 'all' | 'canceled'>('active');
    /*
     * Drag/resize optimistic overlay, keyed by session id. Lives in React
     * state (not just TanStack Query's cache) so the committed position
     * renders on the same frame SessionBlock clears its local drag offset.
     * TanStack Query's observer notification is microtask-batched, which
     * lands *after* React flushes the event handler — leaving a one-frame
     * bounce to the old slot. Holding the override in React state closes
     * that gap: both setStates batch inside the same pointerup handler.
     * The entry is cleared in onSettled once the mutation reconciles.
     */
    const [moves, setMoves] = useState<Record<string, MoveOverride>>({});

    const canSchedule = useAuthStore((s) => s.hasPermission('edit_scheduling'));
    const roleType = useAuthStore((s) => s.user?.roleType);
    const updateMut = useUpdateSession();
    // Server scopes RBTs to their assigned clients; the staff filter adds no
    // value for them since they're only seeing sessions they're part of.
    const showStaffFilter = roleType !== 'RBT';

    const clients = useClientNames();
    const staff = useUserDirectory();
    const sessionTypes = useActiveSessionTypes();
    const activeClients = useMemo(
        () => (clients.data ?? [])
            .filter((c) => c.status === 'Active')
            .sort((a, b) => `${a.first} ${a.last}`.localeCompare(`${b.first} ${b.last}`)),
        [clients.data],
    );

    // Lookup: session type id → color. Passed to SessionBlock so every block
    // is tinted with its type's color without each block re-running the find.
    const typeColorById = useMemo(() => {
        const m = new Map<string, string>();
        for (const t of sessionTypes.data ?? []) m.set(t.id, t.color);
        return m;
    }, [sessionTypes.data]);

    const today = useMemo(() => new Date(), []);
    // weekDates returns the period *containing* today, so offset 0 always
    // includes today — including on a Sunday, where the Sun–Sat week is the
    // one starting that day, not the week that just ended.
    const days = useMemo(() => weekDates(today, offset, view), [today, offset, view]);

    const { start: startHour, end: endHour } = useCalendarHours();
    const totalSlots = Math.max(1, (endHour - startHour) * 4);

    const range = useMemo(
        () => ({ dateFrom: toIsoDate(days[0]), dateTo: toIsoDate(days[days.length - 1]) }),
        [days],
    );
    const { data: sessions, isLoading, isError } = useSessions(range);

    const sessionsByDay = useMemo(() => {
        const map = new Map<string, SessionRecord[]>();
        for (const d of days) map.set(toIsoDate(d), []);
        if (sessions) {
            for (const s of sessions) {
                const override = moves[s.id];
                const effective = override ? { ...s, ...override } : s;
                // Recurring templates come back with date=null from the server;
                // they're edited via the series scope dialog, not drawn on a date cell.
                if (!effective.date) continue;
                if (staffFilter && effective.staffId !== staffFilter) continue;
                if (clientFilter && effective.clientId !== clientFilter) continue;
                const isCanceled = effective.status === 'Canceled';
                if (statusFilter === 'active' && isCanceled) continue;
                if (statusFilter === 'canceled' && !isCanceled) continue;
                const bucket = map.get(effective.date);
                if (bucket) bucket.push(effective);
            }
        }
        return map;
    }, [days, sessions, moves, staffFilter, clientFilter, statusFilter]);

    const rangeLabel = useMemo(() => {
        const fmt = new Intl.DateTimeFormat(undefined, {
            month: 'short',
            day: 'numeric',
            year: 'numeric',
        });
        if (days.length === 1) return fmt.format(days[0]);
        const first = days[0];
        const last = days[days.length - 1];
        const sameMonth = first.getMonth() === last.getMonth()
            && first.getFullYear() === last.getFullYear();
        if (sameMonth) {
            const monthYear = new Intl.DateTimeFormat(undefined, { month: 'short', year: 'numeric' }).format(first);
            return `${first.getDate()} – ${last.getDate()} ${monthYear}`;
        }
        return `${fmt.format(first)} – ${fmt.format(last)}`;
    }, [days]);

    function handleDayColumnClick(e: React.MouseEvent<HTMLDivElement>, d: Date) {
        if (!canSchedule) return;
        // Clicks on a session tile bubble up from the <button>. Skip those —
        // the tile has its own handler.
        const target = e.target as HTMLElement;
        if (target.closest('[data-session-block="true"]')) return;

        const rect = e.currentTarget.getBoundingClientRect();
        const yWithin = e.clientY - rect.top;
        const slotIndex = Math.max(0, Math.floor(yWithin / SLOT_HEIGHT_PX));
        const hour = startHour + Math.floor(slotIndex / 4);
        const minute = (slotIndex % 4) * 15;
        if (hour >= endHour) return;

        setDialog({ kind: 'create', slot: { date: toIsoDate(d), hour, minute } });
    }

    function openToolbarCreate() {
        // Toolbar "+ Schedule" prefills today (if visible in the range, else
        // the first day of the view) at 9:00 — matches what a user typically
        // means by "schedule a session right now".
        const todayIso = toIsoDate(today);
        const inRange = days.some((d) => toIsoDate(d) === todayIso);
        const date = inRange ? todayIso : toIsoDate(days[0]);
        setDialog({ kind: 'create', slot: { date, hour: 9, minute: 0 } });
    }

    function clearMove(id: string) {
        setMoves((prev) => {
            if (!(id in prev)) return prev;
            const copy = { ...prev };
            delete copy[id];
            return copy;
        });
    }

    function handleDragEnd(
        session: SessionRecord,
        next: { date: string; startH: number; startM: number },
    ) {
        // Drag reschedules the single occurrence even for series members —
        // moving "this and all following" off their original cadence belongs
        // in the scope dialog, not a drag.
        setMoves((prev) => ({
            ...prev,
            [session.id]: {
                date: next.date,
                startH: next.startH,
                startM: next.startM,
                dur: session.dur,
            },
        }));
        updateMut.mutate(
            {
                id: session.id,
                payload: { date: next.date, startH: next.startH, startM: next.startM },
            },
            { onSettled: () => clearMove(session.id) },
        );
    }

    function handleResizeEnd(
        session: SessionRecord,
        next: { edge: 'top' | 'bottom'; startH: number; startM: number; dur: number },
    ) {
        // Edge resize commits a per-occurrence update; bulk-series resize
        // belongs in the scope dialog for the same reason as drag.
        setMoves((prev) => ({
            ...prev,
            [session.id]: {
                date: session.date ?? '',
                startH: next.startH,
                startM: next.startM,
                dur: next.dur,
            },
        }));
        updateMut.mutate(
            {
                id: session.id,
                payload: { startH: next.startH, startM: next.startM, dur: next.dur },
            },
            { onSettled: () => clearMove(session.id) },
        );
    }

    function changeView(next: CalendarView) {
        // Keep the user on the week they were looking at. Week↔Workweek share a
        // unit so the offset carries over untouched; switching into/out of Day
        // view converts the unit (offsetForView) so it doesn't jump 10 weeks
        // when coming from a day offset of 70, nor snap back to the current week.
        setOffset((cur) => offsetForView(today, cur, view, next));
        setView(next);
    }

    return (
        <div className="p-6 max-w-[1400px] mx-auto h-full flex flex-col">
            <div className="flex items-center justify-between mb-4 shrink-0">
                <h1 className="font-display text-2xl font-bold text-text">Scheduling</h1>
                {canSchedule && (
                    <Button variant="primary" size="sm" onClick={openToolbarCreate}>
                        <Plus className="w-3.5 h-3.5" /> Schedule
                    </Button>
                )}
            </div>

            <Card className="p-0 overflow-hidden flex-1 min-h-0 flex flex-col">
                <div className="flex flex-wrap items-center gap-3 p-3 border-b border-border">
                    <div role="tablist" aria-label="Calendar view" className="flex items-center gap-1 p-0.5 bg-surface-muted rounded-md">
                        {VIEW_OPTIONS.map((opt) => (
                            <button
                                key={opt.value}
                                type="button"
                                role="tab"
                                aria-selected={view === opt.value}
                                onClick={() => changeView(opt.value)}
                                className={cn(
                                    'px-3 py-1 text-xs rounded transition-colors',
                                    view === opt.value
                                        ? 'bg-surface text-text shadow-xs font-medium'
                                        : 'text-text-muted hover:text-text',
                                )}
                            >
                                {opt.label}
                            </button>
                        ))}
                    </div>

                    <div className="flex items-center gap-1">
                        <Button
                            variant="ghost"
                            size="sm"
                            aria-label="Previous"
                            onClick={() => setOffset((o) => o - 1)}
                        >
                            <ChevronLeft className="w-3.5 h-3.5" />
                        </Button>
                        <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setOffset(0)}
                            disabled={offset === 0}
                        >
                            Today
                        </Button>
                        <Button
                            variant="ghost"
                            size="sm"
                            aria-label="Next"
                            onClick={() => setOffset((o) => o + 1)}
                        >
                            <ChevronRight className="w-3.5 h-3.5" />
                        </Button>
                    </div>

                    <div className="text-sm font-medium text-text">{rangeLabel}</div>

                    <div className="ml-auto flex flex-wrap items-center gap-3">
                        {isLoading && (
                            <span className="inline-flex items-center gap-1 text-xs text-text-subtle" aria-live="polite" aria-label="Loading sessions">
                                <Skeleton className="h-3 w-16" />
                            </span>
                        )}
                        {isError && (
                            <span className="text-xs text-error">Couldn't load sessions</span>
                        )}

                        {showStaffFilter && (
                            <SelectField
                                className="h-7 py-0 text-xs w-36"
                                value={staffFilter}
                                onChange={(e) => setStaffFilter(e.target.value)}
                                aria-label="Filter by staff"
                            >
                                <option value="">All staff</option>
                                {(staff.data ?? []).map((u) => (
                                    <option key={u.id} value={u.id}>
                                        {u.first} {u.last}
                                        {u.roleType === 'BCBA' || u.roleType === 'RBT' ? ` · ${u.roleType}` : ''}
                                    </option>
                                ))}
                            </SelectField>
                        )}

                        <SelectField
                            className="h-7 py-0 text-xs w-36"
                            value={clientFilter}
                            onChange={(e) => setClientFilter(e.target.value)}
                            aria-label="Filter by client"
                        >
                            <option value="">All clients</option>
                            {activeClients.map((c) => (
                                <option key={c.id} value={c.id}>
                                    {c.first} {c.last}
                                </option>
                            ))}
                        </SelectField>

                        <SelectField
                            className="h-7 py-0 text-xs w-32"
                            value={statusFilter}
                            onChange={(e) => setStatusFilter(e.target.value as typeof statusFilter)}
                            aria-label="Filter by status"
                        >
                            <option value="active">Active</option>
                            <option value="all">All</option>
                            <option value="canceled">Canceled</option>
                        </SelectField>

                        {(staffFilter || clientFilter || statusFilter !== 'active') && (
                            <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => {
                                    setStaffFilter('');
                                    setClientFilter('');
                                    setStatusFilter('active');
                                }}
                            >
                                Clear filters
                            </Button>
                        )}
                    </div>
                </div>

                <div className="flex-1 min-h-0 overflow-auto">
                    <div
                        className="grid min-w-[640px]"
                        style={{
                            gridTemplateColumns: `56px repeat(${days.length}, minmax(0, 1fr))`,
                        }}
                    >
                        <div
                            className="sticky top-0 z-10 bg-accent-soft-solid border-b border-border h-9"
                            aria-hidden
                        />
                        {days.map((d) => {
                            const head = formatDayHeader(d);
                            const isToday = isSameDay(d, today);
                            return (
                                <div
                                    key={d.toISOString()}
                                    className={cn(
                                        // Day strip reads as a distinct row from the grid body.
                                        // Default: light green (accent-soft-solid — opaque
                                        // equivalent of accent-soft, needed so scrolling blocks
                                        // don't bleed through the sticky header). Today: full
                                        // accent green — same hue, denser shade, so the eye
                                        // jumps to today without having to read the date.
                                        'sticky top-0 z-10 border-b border-l border-border h-9 px-2 flex items-center justify-between text-2xs',
                                        isToday ? 'bg-accent' : 'bg-accent-soft-solid',
                                    )}
                                >
                                    <span className={cn('uppercase tracking-wider font-bold', isToday ? 'text-text' : 'text-text-muted')}>
                                        {head.label}
                                    </span>
                                    <span className={cn('tabular-nums font-bold', isToday ? 'text-text' : 'text-text-muted')}>
                                        {head.date}
                                    </span>
                                </div>
                            );
                        })}

                        <div className="relative border-r border-border">
                            {Array.from({ length: endHour - startHour }, (_, i) => {
                                const h = startHour + i;
                                return (
                                    // No `border-b` here — the day columns' own
                                    // solid hour borders span across the grid.
                                    // Having both meant the browser rounded two
                                    // 1px lines at the same y independently, and
                                    // at some zoom levels they landed a pixel
                                    // apart, making specific hour lines look
                                    // offset from their label.
                                    <div
                                        key={h}
                                        className="h-16 px-2 pt-1 text-2xs text-text-subtle text-right tabular-nums"
                                    >
                                        {formatHour(h)}
                                    </div>
                                );
                            })}
                        </div>

                        {days.map((d) => {
                            const iso = toIsoDate(d);
                            const daySessions = sessionsByDay.get(iso) ?? [];
                            const laned = layoutLanes(daySessions, startHour, endHour);
                            return (
                                // Mouse-position click-to-create shortcut. WCAG SC 2.1.1 is met
                                // via the keyboard-accessible "+ Schedule" button in the toolbar
                                // which exposes the same date+time pickers.
                                // eslint-disable-next-line jsx-a11y/no-static-element-interactions, jsx-a11y/click-events-have-key-events
                                <div
                                    key={d.toISOString()}
                                    data-day-column={iso}
                                    className={cn(
                                        'relative border-l border-border',
                                        canSchedule && 'cursor-pointer',
                                    )}
                                    style={{ minHeight: `${totalSlots * SLOT_HEIGHT_PX}px` }}
                                    onClick={(e) => handleDayColumnClick(e, d)}
                                >
                                    {Array.from({ length: totalSlots }, (_, s) => (
                                        <div
                                            key={s}
                                            className={cn(
                                                // Per-slot hover shading mirrors the legacy
                                                // calendar cue. Session blocks sit in an
                                                // absolutely-positioned wrapper above these
                                                // slots; hover over empty area passes through
                                                // because the wrapper is pointer-events-none.
                                                'h-4 border-border transition-colors hover:bg-accent-soft/60',
                                                s % 4 === 3 ? 'border-b' : 'border-b border-dashed border-border/30',
                                            )}
                                        />
                                    ))}
                                    {/*
                                     * Right-side click gap: session blocks are positioned inside
                                     * a narrower inner region, leaving the outer 24px strip
                                     * always clickable for new-session creation even on dense
                                     * days. Ported from the legacy app (2026-04-18 UX fix).
                                     */}
                                    <div className="absolute inset-y-0 left-0 right-6 pointer-events-none">
                                        {isLoading ? (
                                            <div className="p-1 flex flex-col gap-1 pt-2">
                                                {[40, 64, 32].map((h, i) => (
                                                    <Skeleton key={i} className={`rounded w-full opacity-50`} style={{ height: h }} />
                                                ))}
                                            </div>
                                        ) : laned.map((item) => (
                                            <SessionBlock
                                                key={item.sess.id}
                                                session={item.sess}
                                                placement={{
                                                    startSlot: item.startSlot,
                                                    heightSlots: item.heightSlots,
                                                    lane: item.lane,
                                                    laneCount: item.laneCount,
                                                }}
                                                slotHeightPx={SLOT_HEIGHT_PX}
                                                gridStartHour={startHour}
                                                gridEndHour={endHour}
                                                typeColor={item.sess.serviceTypeId ? typeColorById.get(item.sess.serviceTypeId) : undefined}
                                                onClick={(s) => navigate(`/sessions/${s.id}`)}
                                                onDragEnd={canSchedule ? handleDragEnd : undefined}
                                                onResizeEnd={canSchedule ? handleResizeEnd : undefined}
                                            />
                                        ))}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </Card>

            {!isLoading && !isError && sessions !== undefined && sessions.length === 0 && canSchedule && (
                <div className="mt-4 flex flex-col items-center gap-2 text-center text-text-subtle">
                    <CalendarDays className="w-7 h-7" aria-hidden />
                    <p className="text-sm font-medium text-text">No sessions this week</p>
                    <p className="text-xs">Click any time slot or use the Schedule button to add one.</p>
                </div>
            )}

            {/* Only mount SessionDialog for users who can actually create
                sessions. Otherwise its useUserDirectory / useUserDirectory(BCBA)
                / useClientsList hooks fire on every page load for RBTs and
                other view-only roles, even though they can't open the dialog.
                Note: the picker on this page itself uses the lighter
                useClientNames hook (id/first/last/status only); SessionDialog
                still needs useClientsList for the selected client's auth +
                availability warnings. */}
            {canSchedule && (
                <SessionDialog
                    open={dialog.kind !== 'closed'}
                    onOpenChange={(open) => { if (!open) setDialog({ kind: 'closed' }); }}
                    defaultDate={dialog.kind === 'create' ? dialog.slot.date : undefined}
                    defaultHour={dialog.kind === 'create' ? dialog.slot.hour : undefined}
                    defaultMinute={dialog.kind === 'create' ? dialog.slot.minute : undefined}
                />
            )}
        </div>
    );
}
